/**
 * LayoutContext
 *
 * Provides a mechanism for child pages to override layout constraints.
 * Pages can opt into full-width mode to break out of the default max-w-5xl container.
 */

import { createContext, useContext, useState, useCallback, useMemo, useEffect, type ReactNode } from "react";

type LayoutContextValue = {
  /** Whether the current page should render full-width (no max-w constraint) */
  fullWidth: boolean;
  /** Set full-width mode - call with `true` in useEffect, cleanup with `false` */
  setFullWidth: (value: boolean) => void;
  /** Whether <main> padding should be stripped for edge-to-edge canvas pages */
  bleed: boolean;
  /** Set bleed mode - strips px/py from <main> so canvas owns all spacing */
  setBleed: (value: boolean) => void;
};

const LayoutContext = createContext<LayoutContextValue | null>(null);

export const LayoutProvider = ({ children }: { children: ReactNode }) => {
  const [fullWidth, setFullWidthState] = useState(false);
  const [bleed, setBleedState] = useState(false);

  const setFullWidth = useCallback((value: boolean) => {
    setFullWidthState(value);
  }, []);

  const setBleed = useCallback((value: boolean) => {
    setBleedState(value);
  }, []);

  const value = useMemo(() => ({ fullWidth, setFullWidth, bleed, setBleed }), [fullWidth, setFullWidth, bleed, setBleed]);

  return <LayoutContext.Provider value={value}>{children}</LayoutContext.Provider>;
};

/**
 * Hook to access layout context.
 * Returns `null` if used outside LayoutProvider (safe fallback).
 */
export const useLayoutContext = (): LayoutContextValue | null => {
  return useContext(LayoutContext);
};

/**
 * Hook for pages to declare they need full-width layout.
 * Automatically cleans up on unmount.
 *
 * @example
 * ```tsx
 * const MyPage = () => {
 *   useFullWidthLayout();
 *   return <div className="max-w-[1800px] mx-auto">...</div>;
 * };
 * ```
 */
export const useFullWidthLayout = () => {
  const ctx = useLayoutContext();
  const setFullWidth = ctx?.setFullWidth;

  useEffect(() => {
    setFullWidth?.(true);
    return () => {
      setFullWidth?.(false);
    };
  }, [setFullWidth]);
};

/**
 * Hook for canvas pages that need zero padding on <main>.
 * Strips px-6 py-6 lg:px-8 so the page controls all edge spacing.
 * Automatically implies full-width mode.
 */
export const useBleedLayout = () => {
  const ctx = useLayoutContext();
  const setFullWidth = ctx?.setFullWidth;
  const setBleed = ctx?.setBleed;

  useEffect(() => {
    setFullWidth?.(true);
    setBleed?.(true);
    return () => {
      setFullWidth?.(false);
      setBleed?.(false);
    };
  }, [setFullWidth, setBleed]);
};
